/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PaginationItem from '@mui/material/PaginationItem';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import ProduceDialog from '../../Components/ProduceDialog/ProduceDialog';
import { inventoryAPI } from '../../services/apiService';
import './Inventory.css';

const UNIT_OPTIONS = [
    { value: 'kg', labelKey: 'unit_kg' },
    { value: 'g', labelKey: 'unit_g' },
    { value: 'liter', labelKey: 'unit_liter' },
    { value: 'ml', labelKey: 'unit_ml' },
    { value: 'piece', labelKey: 'unit_piece' },
    { value: 'pack', labelKey: 'unit_pack' },
];

const emptyInventoryForm = {
    name: '',
    unit: '',
    quantity: '',
    unit_value: '',
};

function Inventory() {
    const { t, i18n } = useTranslation();
    const location = useLocation();

    const [inventories, setInventories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [openProduceDialog, setOpenProduceDialog] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editingInventory, setEditingInventory] = useState(null);

    const [openActionDialog, setOpenActionDialog] = useState(false);
    const [actionType, setActionType] = useState(null);
    const [actionInventory, setActionInventory] = useState(null);
    const [actionForm, setActionForm] = useState({
        amount: '',
        price: '',
    });

    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [newInventory, setNewInventory] = useState(emptyInventoryForm);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const formatNumberByLanguage = (num) => {
        const locale = i18n.language === 'fa' ? 'fa-IR' : 'en-US';
        return new Intl.NumberFormat(locale).format(num);
    };

    const handleChangePage = (_, value) => {
        setCurrentPage(value);
    };

    const fetchInventories = async () => {
        if (!localStorage.getItem('token') || !localStorage.getItem('userGroup')) {
            setError(t('auth_or_group_missing'));
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const result = await inventoryAPI.getAll({ fallback: [] });
            setInventories(Array.isArray(result) ? result : []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (location.state?.successMessage) {
            setSnackbar({
                open: true,
                message: location.state.successMessage,
                severity: 'success',
            });
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        fetchInventories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const confirmDelete = (id) => {
        setSelectedId(id);
        setOpenDeleteDialog(true);
    };

    const handleCloseDelete = () => {
        setOpenDeleteDialog(false);
        setSelectedId(null);
    };

    const handleConfirmDelete = async () => {
        if (!selectedId) return;

        try {
            await inventoryAPI.remove(selectedId);
            setInventories((prev) => prev.filter((item) => item.id !== selectedId));

            setSnackbar({
                open: true,
                message: t('delete_inventory_success'),
                severity: 'success',
            });
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: t('delete_inventory_error'),
                severity: 'error',
            });
        } finally {
            handleCloseDelete();
        }
    };

    const handleEdit = (item) => {
        setEditingInventory({
            id: item.id,
            name: item.name || '',
            unit: item.unit || '',
            quantity: item.quantity ?? '',
            unit_value: item.unit_value ?? '',
        });
        setOpenEditDialog(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditingInventory((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSaveEdit = async () => {
        if (!editingInventory) return;

        try {
            const { id } = editingInventory;

            const payload = {
                name: editingInventory.name,
                unit: editingInventory.unit,
            };

            if (editingInventory.quantity !== '') {
                payload.quantity = Number(editingInventory.quantity);
            }

            if (editingInventory.unit_value !== '') {
                payload.unit_value = Number(editingInventory.unit_value);
            }

            await inventoryAPI.update(id, payload);
            await fetchInventories();

            setSnackbar({
                open: true,
                message: t('changes_saved_success'),
                severity: 'success',
            });

            setOpenEditDialog(false);
            setEditingInventory(null);
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: t('edit_inventory_error'),
                severity: 'error',
            });
        }
    };

    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setNewInventory((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCloseCreateDialog = () => {
        if (createLoading) return;

        setOpenCreateDialog(false);
        setNewInventory(emptyInventoryForm);
    };

    const handleCreateInventory = async () => {
        if (!newInventory.name || !newInventory.unit) {
            setSnackbar({
                open: true,
                message: t('fill_required_fields'),
                severity: 'error',
            });
            return;
        }

        try {
            setCreateLoading(true);

            const payload = {
                name: newInventory.name,
                unit: newInventory.unit,
            };

            if (newInventory.quantity !== '') {
                payload.quantity = Number(newInventory.quantity);
            }

            if (newInventory.unit_value !== '') {
                payload.unit_value = Number(newInventory.unit_value);
            }

            await inventoryAPI.create(payload);
            await fetchInventories();

            setSnackbar({
                open: true,
                message: t('create_inventory_success'),
                severity: 'success',
            });

            setOpenCreateDialog(false);
            setNewInventory(emptyInventoryForm);
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: t('create_inventory_error'),
                severity: 'error',
            });
        } finally {
            setCreateLoading(false);
        }
    };

    const openInventoryAction = (type, item) => {
        setActionType(type);
        setActionInventory(item);
        setActionForm({
            amount: '',
            price: '',
        });
        setOpenActionDialog(true);
    };

    const closeInventoryAction = () => {
        setActionType(null);
        setActionInventory(null);
        setActionForm({
            amount: '',
            price: '',
        });
        setOpenActionDialog(false);
    };

    const handleActionFormChange = (e) => {
        const { name, value } = e.target;
        setActionForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleConfirmInventoryAction = async () => {
        if (!actionInventory || !actionType || !actionForm.amount) return;

        try {
            if (actionType === 'purchase') {
                const payload = {
                    amount: Number(actionForm.amount),
                };

                if (actionForm.price !== '') {
                    payload.price = Number(actionForm.price);
                }

                await inventoryAPI.addPurchase(actionInventory.id, payload);
            }

            if (actionType === 'waste') {
                const payload = {
                    amount: Number(actionForm.amount),
                };

                if (actionForm.price !== '') {
                    payload.price = Number(actionForm.price);
                }

                await inventoryAPI.addWaste(actionInventory.id, payload);
            }

            await fetchInventories();

            setSnackbar({
                open: true,
                message:
                    actionType === 'purchase'
                        ? t('purchase_added_success')
                        : t('waste_added_success'),
                severity: 'success',
            });

            closeInventoryAction();
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message:
                    actionType === 'purchase' ? t('purchase_added_error') : t('waste_added_error'),
                severity: 'error',
            });
        }
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;

        setSnackbar((prev) => ({
            ...prev,
            open: false,
        }));
    };
    const getUnitLabel = (unitValue) => {
        const unit = UNIT_OPTIONS.find((option) => option.value === unitValue);
        return unit ? t(unit.labelKey) : unitValue || '-';
    };
    const totalPages = Math.ceil(inventories.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = inventories.slice(indexOfFirstItem, indexOfLastItem);
    const handleProductionSuccess = async () => {
        await fetchInventories();
        setSnackbar({
            open: true,
            message: t('production_success'),
            severity: 'success',
        });
    };

    return (
        <div className="inventories_page">
            <Sidebar />

            <div className="inventories_main">
                <Navbar />

                <div className="inventories_content">
                    <div className="inventories_header">
                        <Button
                            className="add_new_inventory"
                            type="button"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenCreateDialog(true)}
                        >
                            {t('add_new_inventory')}
                        </Button>
                        <Button
                            className="produce_inventory"
                            type="button"
                            variant="contained"
                            color="success"
                            onClick={() => setOpenProduceDialog(true)}
                        >
                            {t('produce')}
                        </Button>
                    </div>

                    {loading && <p>{t('loading')}</p>}

                    {error && (
                        <p>
                            {t('error')}: {error}
                        </p>
                    )}

                    {!loading && !error && (
                        <TableContainer component={Paper} className="inventories_table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">{t('inventory_name')}</TableCell>
                                        <TableCell align="center">{t('amount-inv')}</TableCell>
                                        <TableCell align="center">{t('unit')}</TableCell>
                                        <TableCell align="center">{t('unit_value')}</TableCell>
                                        <TableCell align="center">{t('total_value')}</TableCell>
                                        <TableCell align="center">{t('updated_date')}</TableCell>
                                        <TableCell align="center">{t('actions')}</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody className="inventories_table_body">
                                    {currentItems.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell
                                                align="center"
                                                data-label={t('inventory_name')}
                                            >
                                                {item.name || '-'}
                                            </TableCell>

                                            <TableCell align="center" data-label={t('amount-inv')}>
                                                {item.quantity !== null &&
                                                item.quantity !== undefined &&
                                                item.quantity !== ''
                                                    ? Number(item.quantity).toLocaleString(
                                                          i18n.language === 'fa'
                                                              ? 'fa-IR'
                                                              : 'en-US',
                                                          {
                                                              minimumFractionDigits: 0,
                                                              maximumFractionDigits: 2,
                                                          }
                                                      )
                                                    : '-'}
                                            </TableCell>

                                            <TableCell align="center" data-label={t('unit')}>
                                                {getUnitLabel(item.unit)}
                                            </TableCell>

                                            <TableCell align="center" data-label={t('unit_value')}>
                                                {item.unit_value.toLocaleString(
                                                    i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                                                )}
                                            </TableCell>

                                            <TableCell align="center" data-label={t('total_value')}>
                                                {item.total_value.toLocaleString(
                                                    i18n.language === 'fa' ? 'fa-IR' : 'en-us'
                                                )}
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                data-label={t('updated_date')}
                                            >
                                                {item.updated_at
                                                    ? new Date(item.updated_at).toLocaleDateString(
                                                          i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                                                      )
                                                    : '-'}
                                            </TableCell>

                                            <TableCell align="center" className="action_buttons">
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() =>
                                                        openInventoryAction('purchase', item)
                                                    }
                                                    sx={{ marginLeft: '8px' }}
                                                >
                                                    {t('purchase')}
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    color="warning"
                                                    onClick={() =>
                                                        openInventoryAction('waste', item)
                                                    }
                                                    sx={{ marginLeft: '8px' }}
                                                >
                                                    {t('waste')}
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => confirmDelete(item.id)}
                                                    sx={{ marginLeft: '8px' }}
                                                >
                                                    {t('delete')}
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    color="success"
                                                    onClick={() => handleEdit(item)}
                                                >
                                                    {t('edit')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {!loading && !error && totalPages > 1 && (
                        <div className="orders_pagination">
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handleChangePage}
                                color="primary"
                                variant="outlined"
                                hidePrevButton
                                hideNextButton
                                renderItem={(item) => (
                                    <PaginationItem
                                        // eslint-disable-next-line react/jsx-props-no-spreading
                                        {...item}
                                        page={
                                            item.page
                                                ? formatNumberByLanguage(item.page)
                                                : item.page
                                        }
                                    />
                                )}
                            />
                        </div>
                    )}

                    <Dialog open={openDeleteDialog} onClose={handleCloseDelete}>
                        <DialogTitle>{t('confirm_delete')}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>{t('confirm_delete_inventory')}</DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDelete} color="primary">
                                {t('no')}
                            </Button>
                            <Button onClick={handleConfirmDelete} color="error" autoFocus>
                                {t('yes_delete')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openEditDialog}
                        onClose={() => setOpenEditDialog(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>{t('edit_inventory')}</DialogTitle>

                        <DialogContent>
                            {editingInventory && (
                                <>
                                    <TextField
                                        margin="dense"
                                        label={t('inventory_name')}
                                        name="name"
                                        value={editingInventory.name}
                                        onChange={handleEditChange}
                                        fullWidth
                                        variant="outlined"
                                    />

                                    <FormControl fullWidth margin="dense">
                                        <InputLabel>{t('unit')}</InputLabel>
                                        <Select
                                            name="unit"
                                            label={t('unit')}
                                            value={editingInventory.unit}
                                            onChange={handleEditChange}
                                        >
                                            {UNIT_OPTIONS.map((unit) => (
                                                <MenuItem key={unit.value} value={unit.value}>
                                                    {t(unit.labelKey)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        margin="dense"
                                        label={t('amount-inv')}
                                        name="quantity"
                                        type="number"
                                        value={editingInventory.quantity}
                                        onChange={handleEditChange}
                                        fullWidth
                                        variant="outlined"
                                    />

                                    <TextField
                                        margin="dense"
                                        label={t('unit_value')}
                                        name="unit_value"
                                        type="number"
                                        value={editingInventory.unit_value}
                                        onChange={handleEditChange}
                                        fullWidth
                                        variant="outlined"
                                    />
                                </>
                            )}
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={() => setOpenEditDialog(false)}>{t('cancel')}</Button>
                            <Button onClick={handleSaveEdit} color="success">
                                {t('save_changes')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openActionDialog}
                        onClose={closeInventoryAction}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>
                            {actionType === 'purchase' ? t('add_purchase') : t('add_waste')}
                        </DialogTitle>

                        <DialogContent>
                            <DialogContentText sx={{ marginBottom: '12px' }}>
                                {actionInventory?.name}
                            </DialogContentText>

                            <TextField
                                margin="dense"
                                label={t('amount-inv')}
                                name="amount"
                                type="number"
                                value={actionForm.amount}
                                onChange={handleActionFormChange}
                                fullWidth
                                variant="outlined"
                            />

                            {actionType === 'purchase' && (
                                <TextField
                                    margin="dense"
                                    label={t('price')}
                                    name="price"
                                    type="number"
                                    value={actionForm.price}
                                    onChange={handleActionFormChange}
                                    fullWidth
                                    variant="outlined"
                                />
                            )}
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={closeInventoryAction}>{t('cancel')}</Button>
                            <Button
                                onClick={handleConfirmInventoryAction}
                                color={actionType === 'purchase' ? 'primary' : 'warning'}
                            >
                                {t('confirm')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openCreateDialog}
                        onClose={handleCloseCreateDialog}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>{t('add_new_inventory')}</DialogTitle>

                        <DialogContent>
                            <TextField
                                margin="dense"
                                label={t('inventory_name')}
                                name="name"
                                value={newInventory.name}
                                onChange={handleCreateChange}
                                fullWidth
                                variant="outlined"
                            />

                            <FormControl fullWidth margin="dense">
                                <InputLabel>{t('unit')}</InputLabel>
                                <Select
                                    name="unit"
                                    label={t('unit')}
                                    value={newInventory.unit}
                                    onChange={handleCreateChange}
                                >
                                    {UNIT_OPTIONS.map((unit) => (
                                        <MenuItem key={unit.value} value={unit.value}>
                                            {t(unit.labelKey)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                margin="dense"
                                label={t('amount-inv')}
                                name="quantity"
                                type="number"
                                value={newInventory.quantity}
                                onChange={handleCreateChange}
                                fullWidth
                                variant="outlined"
                            />

                            <TextField
                                margin="dense"
                                label={t('unit_value')}
                                name="unit_value"
                                type="number"
                                value={newInventory.unit_value}
                                onChange={handleCreateChange}
                                fullWidth
                                variant="outlined"
                            />
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={handleCloseCreateDialog} disabled={createLoading}>
                                {t('cancel')}
                            </Button>
                            <Button
                                onClick={handleCreateInventory}
                                color="primary"
                                variant="contained"
                                disabled={createLoading}
                            >
                                {createLoading ? t('loading') : t('confirm')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <ProduceDialog
                        open={openProduceDialog}
                        onClose={() => setOpenProduceDialog(false)}
                        onProduced={handleProductionSuccess}
                        inventories={inventories}
                    />

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
}

export default Inventory;
